import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import io from 'socket.io-client';
import QRCode from 'react-qr-code';
import { FaCheckCircle, FaSpinner, FaCloudUploadAlt, FaPaperPlane, FaPlus, FaWhatsapp, FaUserCircle, FaBolt, FaShieldAlt, FaFileCsv, FaCheckDouble, FaArrowRight, FaUsers, FaChartLine, FaChartBar, FaHeartbeat, FaCommentDots, FaBell, FaHome, FaCog, FaAddressBook, FaTimes, FaDownload } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const socket = io("https://goyee.onrender.com", {
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    transports: ["polling", "websocket"]
});

export default function WhatsAppAuth() {
    const location = useLocation();
    const navigate = useNavigate();
    // When the Navbar's "Messages" item navigates here from another page,
    // it passes { openMessages: true } via router state. Reading it here
    // (instead of waiting for a delayed window event) lets this component
    // render its connecting/messages view on the very first paint, instead
    // of first flashing the Home hero section and then swapping.
    const openedFromMessagesNav = !!(location.state && location.state.openMessages);

    const [qrCode, setQrCode] = useState("");
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [connectedUser, setConnectedUser] = useState(null);
    const [showFormOnly, setShowFormOnly] = useState(false); 
    const [failedList, setFailedList] = useState([]);
    const [startClicked, setStartClicked] = useState(openedFromMessagesNav);
    const [message, setMessage] = useState("");
    const [isScheduling, setIsScheduling] = useState(false);
    const [scheduleDate, setScheduleDate] = useState("");
    const [scheduleHour, setScheduleHour] = useState("12");
    const [scheduleMinute, setScheduleMinute] = useState("00");
    const [scheduleAmPm, setScheduleAmPm] = useState("AM");
    const [connectionStatus, setConnectionStatus] = useState("connecting");
    const [userName, setUserName] = useState("User"); 
    const [manualContacts, setManualContacts] = useState([""]); 
    const [excelFile, setExcelFile] = useState(null);
    const [mediaFile, setMediaFile] = useState(null);
    // Drives the send-progress popup: null when no send is in flight,
    // otherwise { total, sent, failed, pending, done }.
    const [sendProgress, setSendProgress] = useState(null);
    // Tracks whether the current auth flow was triggered by the Navbar's
    // "Messages" item (an existing/valid session) vs. a fresh "Connect
    // Device" click, so we know whether to skip straight to the chat/
    // messages panel or keep showing the normal QR -> Success animation.
    const messagesRequestedRef = useRef(openedFromMessagesNav);
    // Refs to the native file inputs for Excel/CSV and Media uploads, used
    // only to clear the input's value when a selected file is removed (so
    // selecting the same file again still fires a change event).
    const excelInputRef = useRef(null);
    const mediaInputRef = useRef(null);

    // Consume the one-shot "openMessages" navigation state so refreshing
    // this page or using browser back/forward doesn't keep re-triggering
    // this same startup behavior.
    useEffect(() => {
        if (location.state && location.state.openMessages) {
            navigate(location.pathname, { replace: true, state: {} });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Lets the unified Navbar's "Home" item reset this page back to the
    // landing/hero view when we're already on this route (e.g. after
    // opening Messages here, then clicking Home again). This only resets
    // which section of this page is shown — it does not touch
    // isAuthenticated, qrCode, or the underlying socket connection, so the
    // WhatsApp session stays connected.
    useEffect(() => {
        const handleGoHome = () => {
            setStartClicked(false);
            setShowFormOnly(false);
            messagesRequestedRef.current = false;
        };
        window.addEventListener("gy:go-home", handleGoHome);
        return () => window.removeEventListener("gy:go-home", handleGoHome);
    }, []);

    const [currentEmail, setCurrentEmail] = useState(localStorage.getItem("email") || "");

    useEffect(() => {
        const updateName = () => {
            const storedName = localStorage.getItem("username") || localStorage.getItem("name");
            setUserName(storedName || "User");
        };
        updateName();
        window.addEventListener("loginStatusChanged", updateName);

        const handleEmailChange = () => {
            const email = localStorage.getItem("email") || "";
            if (email !== currentEmail) {
                setCurrentEmail(email);
                setQrCode("");
                setIsAuthenticated(false);
                setConnectedUser(null);
                setStartClicked(false);
                setShowFormOnly(false);
                if (email) {
                    socket.emit("register_email", email);
                    socket.emit("check_status");
                }
            }
        };

        const email = localStorage.getItem("email");
        if (email) {
            socket.emit("register_email", email);
            socket.emit("check_status");
        }

        window.addEventListener("loginStatusChanged", handleEmailChange);

        return () => {
            window.removeEventListener("loginStatusChanged", updateName);
            window.removeEventListener("loginStatusChanged", handleEmailChange);
        };
    }, [currentEmail]);

    useEffect(() => {
        const handleConnect = () => {
            setConnectionStatus("connected");
            socket.emit("check_status");
        };

        const handleDisconnect = () => {
            setConnectionStatus("disconnected");
        };

        const handleConnectError = () => {
            setConnectionStatus("disconnected");
        };

        socket.on("connect", handleConnect);
        socket.on("disconnect", handleDisconnect);
        socket.on("connect_error", handleConnectError);
        socket.on("qr", (qr) => setQrCode(qr));
        
        socket.on("ready", (data) => {
            setIsAuthenticated(true);
            if (data && data.user) {
                setConnectedUser(data.user);
            }
            // If this "ready" was in response to opening the Messages page
            // from the Navbar (existing session), skip the Success screen
            // and go straight to the chat/messages panel.
            if (messagesRequestedRef.current) {
                setShowFormOnly(true);
                messagesRequestedRef.current = false;
            }
        });

        socket.on("logout", () => {
            setIsAuthenticated(false);  // Removes 'Linked Successfully'
            setShowFormOnly(false);     // Hides the message form
            setQrCode("");              // Clears old QR so loader shows until new QR comes
            setConnectedUser(null);     // Clear connected user
            messagesRequestedRef.current = false;
        });

        // Setup listeners for bulk message sending progress from the backend.
        const handleBulkProgressStart = (payload) => {
            setSendProgress({
                total: payload.total || 0,
                processed: 0,
                sent: 0,
                failed: 0,
                currentNumber: "Initializing...",
                done: false
            });
        };

        const handleBulkProgressUpdate = (payload) => {
            if (payload.status === "failed") {
                setFailedList((prevList) => [
                    ...prevList,
                    {
                        number: payload.number,
                        reason: payload.reason || "Unable to send message",
                        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                    }
                ]);
            }

            if (payload.status === "sent") {
                let currentSent = parseInt(localStorage.getItem("totalSent") || "0", 10);
                const newTotalSent = currentSent + 1;
                localStorage.setItem("totalSent", newTotalSent);

                const storedEmail = localStorage.getItem("email");
                if (storedEmail) {
                    fetch("https://goyee.onrender.com/api/user/increment-sent", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email: storedEmail })
                    }).catch(e => console.error(e));
                }

                if (newTotalSent % 2 === 0) {
                    let currentCredits = parseInt(localStorage.getItem("credits") || "0", 10);
                    const newCredits = Math.max(0, currentCredits - 1);
                    localStorage.setItem("credits", newCredits);
                    if (storedEmail) {
                        fetch("https://goyee.onrender.com/api/user/use-credit", {
                            method: "POST",
                            headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ email: storedEmail, amount: 1 })
                        }).catch(e => console.error(e));
                    }
                }
                window.dispatchEvent(new Event("creditsChanged"));
            }

            setSendProgress((prev) => {
                if (!prev || prev.done) return prev;
                let processed = prev.processed;
                let sent = prev.sent;
                let failed = prev.failed;
                let currentNumber = prev.currentNumber;

                if (payload.status === "sending") {
                    currentNumber = payload.number;
                } else if (payload.status === "sent") {
                    processed += 1;
                    sent += 1;
                } else if (payload.status === "failed") {
                    processed += 1;
                    failed += 1;
                }

                return {
                    ...prev,
                    processed,
                    sent,
                    failed,
                    currentNumber
                };
            });
        };

        const handleBulkProgressCompleted = () => {
            setSendProgress((prev) => {
                if (!prev) return prev;
                return {
                    ...prev,
                    processed: prev.total,
                    sent: prev.total - prev.failed,
                    done: true
                };
            });
        };

        socket.on("bulk_progress_start", handleBulkProgressStart);
        socket.on("bulk_progress_update", handleBulkProgressUpdate);
        socket.on("bulk_progress_completed", handleBulkProgressCompleted);

        // This component's local state (isAuthenticated, etc.) resets on
        // every mount, but the underlying socket connection/session is
        // shared across the app and usually stays connected. Re-check the
        // real status here so returning to this page (e.g. via the Navbar's
        // Messages item, or navigating back from Profile/About) reflects
        // the existing WhatsApp session instead of looking "disconnected".
        socket.emit("check_status");

        return () => {
            socket.off("connect", handleConnect);
            socket.off("disconnect", handleDisconnect);
            socket.off("connect_error", handleConnectError);
            socket.off("ready");
            socket.off("logout");
            socket.off("bulk_progress_start", handleBulkProgressStart);
            socket.off("bulk_progress_update", handleBulkProgressUpdate);
            socket.off("bulk_progress_completed", handleBulkProgressCompleted);
        };
    }, []);

    // Timer to transition from "Success" screen to "Message Configuration"
    useEffect(() => {
        if (startClicked && isAuthenticated && !showFormOnly) {
            const timer = setTimeout(() => setShowFormOnly(true), 5000);
            return () => clearTimeout(timer);
        }
    }, [startClicked, isAuthenticated, showFormOnly]);

    // Function to add new input box
    const addContactField = () => {
        setManualContacts([...manualContacts, ""]);
    };

    // Function to remove input box
    const removeContactField = (index) => {
        if (index === 0) return; // Do not delete the first/default input field
        const newContacts = manualContacts.filter((_, i) => i !== index);
        setManualContacts(newContacts);
    };

    // Function to update input value
    const handleContactChange = (index, value) => {
        const newContacts = [...manualContacts];
        newContacts[index] = value;
        setManualContacts(newContacts);
    };

    const handleMediaChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => {
                let mime = file.type;
                if (!mime) {
                    const ext = file.name.toLowerCase().split('.').pop();
                    const mimeMap = {
                        'png': 'image/png',
                        'jpg': 'image/jpeg',
                        'jpeg': 'image/jpeg',
                        'gif': 'image/gif',
                        'webp': 'image/webp',
                        'mp4': 'video/mp4',
                        'mov': 'video/quicktime',
                        'pdf': 'application/pdf',
                        'csv': 'text/csv',
                        'xls': 'application/vnd.ms-excel',
                        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                        'doc': 'application/msword',
                        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'txt': 'text/plain'
                    };
                    mime = mimeMap[ext] || 'application/octet-stream';
                }

                setMediaFile({
                    mimetype: mime,
                    data: reader.result.split(',')[1],
                    filename: file.name
                });
            };
        }
    };

    // Converts already-read file bytes into a base64 string, the same
    // format the existing Media/Document upload already sends over the
    // socket as `media.data`. Used to attach the selected CSV/Excel file
    // itself (not just the parsed phone numbers) to outgoing messages.
    const bytesToBase64 = (bytes) => {
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
            binary += String.fromCharCode(bytes[i]);
        }
        return btoa(binary);
    };

    // Some browsers/OSes report an empty `file.type` for .csv files, so
    // fall back to guessing from the extension the same way a WhatsApp
    // client would need a MIME type to know how to label the attachment.
    const guessSpreadsheetMime = (filename) => {
        const lower = (filename || "").toLowerCase();
        if (lower.endsWith(".csv")) return "text/csv";
        if (lower.endsWith(".xlsx")) return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
        if (lower.endsWith(".xls")) return "application/vnd.ms-excel";
        return "application/octet-stream";
    };

    // Shows the popup immediately with everyone pending, before the send
    // request even goes out.
    const startSendProgress = (total) => {
        setFailedList([]);
        setSendProgress({ total, processed: 0, sent: 0, failed: 0, currentNumber: "Initializing...", done: false });
    };

    // Called from the send_bulk_message ack callback. If the backend's ack
    // includes final sent/failed counts, those win; otherwise, whatever
    // wasn't already reported (via live events) as sent or
    // failed is assumed successful, so the popup always reaches a correct,
    // fully-accounted-for final state.
    const finishSendProgress = (response) => {
        setSendProgress((prev) => {
            if (!prev) return prev;
            if (response && (typeof response.sent === "number" || typeof response.failed === "number")) {
                const sent = typeof response.sent === "number" ? response.sent : prev.sent;
                const failed = typeof response.failed === "number" ? response.failed : prev.failed;
                return { ...prev, processed: sent + failed, sent, failed, done: true };
            }
            return { ...prev, processed: prev.total, sent: prev.total - prev.failed, done: true };
        });
    };

    // Clears a selected file (state + the underlying native input value)
    // without affecting any other upload/send logic.
    const removeExcelFile = (e) => {
        e.stopPropagation();
        setExcelFile(null);
        if (excelInputRef.current) excelInputRef.current.value = "";
    };

    const removeMediaFile = (e) => {
        e.stopPropagation();
        setMediaFile(null);
        if (mediaInputRef.current) mediaInputRef.current.value = "";
    };

    const recipientCount = manualContacts.reduce((count, contact) => {
        if (!contact) return count;
        return count + contact.split(',').filter((num) => num.trim()).length;
    }, 0);

    const sendBulkMessages = () => {
        let currentCredits = parseInt(localStorage.getItem("credits") || "10", 10);
        if (currentCredits <= 0) {
            return alert("Today's free credits have been exhausted. Please wait until tomorrow to receive your next 30 free credits.");
        }

        if (!message && !mediaFile && !excelFile) return alert("Please type a message or attach a media file!");
        
        let allNumbers = [];
        manualContacts.forEach(contact => {
            if (contact) {
                // Support comma-separated numbers in a single input box
                contact.split(',').forEach(num => {
                    if (num.trim()) allNumbers.push(num.trim());
                });
            }
        });

        if (excelFile) {
            const reader = new FileReader();

            reader.onerror = () => {
                console.error("❌ Failed to read the selected CSV/Excel file:", reader.error);
                alert("⚠️ Could not read the selected file. Please try again with a valid CSV or Excel file.");
            };

            reader.onload = (e) => {
              try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames && workbook.SheetNames[0];
                if (!firstSheetName) {
                    throw new Error("The selected file doesn't contain any readable sheet.");
                }
                const sheet = workbook.Sheets[firstSheetName];
                
                // Parse row by row using sheet_to_json to inspect individual cells
                const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
                jsonData.forEach(row => {
                    if (Array.isArray(row)) {
                        row.forEach(cell => {
                            if (cell !== null && cell !== undefined) {
                                const cellStr = String(cell).trim();
                                // Ignore emails
                                if (cellStr.includes('@')) return;
                                // Ignore column headers / long text values
                                if (/[a-zA-Z]{4,}/.test(cellStr)) return;
                                
                                // Keep only digits
                                const cleanNum = cellStr.replace(/\D/g, '');
                                // Accept only valid phone number lengths (10 to 15 digits)
                                if (cleanNum.length >= 10 && cleanNum.length <= 15) {
                                    allNumbers.push(cleanNum);
                                }
                            }
                        });
                    }
                });
                
                // Remove duplicates
                allNumbers = [...new Set(allNumbers)];

                if (allNumbers.length === 0) {
                    alert("⚠️ No valid phone numbers were found in the selected file. Please check the file and try again.");
                    return;
                }

                // Only send the user-selected media file (image/video/document).
                // The CSV/XLSX file itself is NEVER sent as an attachment.
                const attachment = mediaFile || null;
                
                console.log("📤 Sending numbers to backend:", allNumbers);
                if (isScheduling) {
                    if (!scheduleDate) return alert("Please select a date for scheduling");
                    let hour = parseInt(scheduleHour);
                    if (scheduleAmPm === 'PM' && hour < 12) hour += 12;
                    if (scheduleAmPm === 'AM' && hour === 12) hour = 0;
                    const scheduledFor = new Date(`${scheduleDate}T${hour.toString().padStart(2, '0')}:${scheduleMinute}:00`);
                    if (scheduledFor < new Date()) return alert("Scheduled time must be in the future!");
                    
                    fetch("https://goyee.onrender.com//api/schedules", {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ email: localStorage.getItem("email"), contacts: allNumbers, message, media: attachment, scheduledFor })
                    }).then(() => {
                        alert("Message scheduled successfully!");
                        setMessage("");
                        setIsScheduling(false);
                    }).catch(e => console.error(e));
                    return;
                }
                startSendProgress(allNumbers.length);
                socket.emit("send_bulk_message", { numbers: allNumbers, text: message, media: attachment }, (response) => {
                    finishSendProgress(response);
                });
                
                setMessage("");
              } catch (err) {
                console.error("❌ Failed to parse the selected CSV/Excel file:", err);
                alert("⚠️ We couldn't read that file. Please make sure it's a valid CSV or Excel (.csv, .xls, .xlsx) file and try again.");
              }
            };
            reader.readAsArrayBuffer(excelFile);
        } else {
            console.log("📤 Sending numbers to backend:", allNumbers);
            if (isScheduling) {
                if (!scheduleDate) return alert("Please select a date for scheduling");
                let hour = parseInt(scheduleHour);
                if (scheduleAmPm === 'PM' && hour < 12) hour += 12;
                if (scheduleAmPm === 'AM' && hour === 12) hour = 0;
                const scheduledFor = new Date(`${scheduleDate}T${hour.toString().padStart(2, '0')}:${scheduleMinute}:00`);
                if (scheduledFor < new Date()) return alert("Scheduled time must be in the future!");
                
                fetch("https://goyee.onrender.com//api/schedules", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email: localStorage.getItem("email"), contacts: allNumbers, message, media: mediaFile, scheduledFor })
                }).then(() => {
                    alert("Message scheduled successfully!");
                    setMessage("");
                    setIsScheduling(false);
                }).catch(e => console.error(e));
                return;
            }
            startSendProgress(allNumbers.length);
            socket.emit("send_bulk_message", { numbers: allNumbers, text: message, media: mediaFile }, (response) => {
                finishSendProgress(response);
            });
            
            setMessage(""); 
        }
    };

    const handleConnectClick = () => {
        if (localStorage.getItem("isLoggedIn") !== "true") {
            alert("⚠️ Please login first to connect your device!");
        } else {
            setStartClicked(true);
            setQrCode(""); // Show spinner while generating
            socket.emit("request_new_qr");
        }
    };

    // Lets the unified Navbar's "Connect" button trigger this page's connect flow
    useEffect(() => {
        window.addEventListener("gy:connect-device", handleConnectClick);
        return () => window.removeEventListener("gy:connect-device", handleConnectClick);
    }, []);

    // Lets the unified Navbar's "Messages" item reopen this same chat/
    // messages panel. If the WhatsApp session is still valid, it opens
    // straight to the messages panel (no QR re-scan). If the session has
    // actually expired/disconnected, it falls back to the normal QR flow.
    const handleOpenMessages = () => {
        messagesRequestedRef.current = true;
        setStartClicked(true);
        if (isAuthenticated) {
            setShowFormOnly(true);
        } else {
            // Re-confirm status; the "ready" handler above will open the
            // messages panel directly if the session is still valid, or
            // the existing "qr" event will populate a QR code to scan.
            socket.emit("check_status");
        }
    };

    useEffect(() => {
        window.addEventListener("gy:open-messages", handleOpenMessages);
        return () => window.removeEventListener("gy:open-messages", handleOpenMessages);
    }, [isAuthenticated]);

    return (
        <div id="gy-top" className="min-h-screen w-full bg-[#212122] transition-all duration-700 relative overflow-x-hidden overflow-y-visible flex flex-col">

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=Inter:wght@400;500;600;700;800&display=swap');

                #gy-top { font-family: 'Inter', ui-sans-serif, system-ui, sans-serif; }
                .gy-display { font-family: 'Sora', ui-sans-serif, system-ui, sans-serif; }

                @keyframes gyFloat { 0%,100% { transform: translateY(0) translateX(0); } 50% { transform: translateY(-24px) translateX(10px); } }
                @keyframes gyFloatSlow { 0%,100% { transform: translateY(0) translateX(0); } 50% { transform: translateY(20px) translateX(-14px); } }
                @keyframes gyGradientMove { 0%,100% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } }
                @keyframes gyPopIn { 0% { opacity: 0; transform: translateY(28px) scale(0.96); } 100% { opacity: 1; transform: translateY(0) scale(1); } }
                @keyframes gyShine { 0% { transform: translateX(-120%) skewX(-12deg); } 100% { transform: translateX(220%) skewX(-12deg); } }
                @keyframes gyRingPulse { 0% { transform: scale(0.7); opacity: 0.8; } 100% { transform: scale(2.1); opacity: 0; } }
                @keyframes gyScan { 0% { top: 6%; opacity: 0; } 10% { opacity: 1; } 90% { opacity: 1; } 100% { top: 94%; opacity: 0; } }
                @keyframes gyBadgeFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
                @keyframes gyDot { 0%,60%,100% { opacity: 0.3; transform: translateY(0); } 30% { opacity: 1; transform: translateY(-3px); } }
                .gy-gradient-text { background-size: 200% auto; animation: gyGradientMove 6s ease-in-out infinite; }
                .gy-pop { opacity: 0; animation: gyPopIn 0.7s cubic-bezier(0.22,1,0.36,1) forwards; }
                .gy-shine-btn { position: relative; overflow: hidden; }
                .gy-shine-btn .gy-shine { position: absolute; top: 0; left: 0; width: 40%; height: 100%; background: linear-gradient(120deg, transparent, rgba(255,255,255,0.55), transparent); transform: translateX(-120%) skewX(-12deg); }
                .gy-shine-btn:hover .gy-shine { animation: gyShine 0.9s ease forwards; }
                .gy-tilt { transition: transform 0.4s cubic-bezier(0.22,1,0.36,1), box-shadow 0.4s ease; }
                .gy-tilt:hover { transform: translateY(-8px); }
                .gy-ring { position: absolute; inset: 0; border-radius: 9999px; border: 2px solid #25D366; animation: gyRingPulse 2s ease-out infinite; }
                .gy-scan-line { position: absolute; left: 6%; right: 6%; height: 2px; background: linear-gradient(90deg, transparent, #25D366, transparent); animation: gyScan 2.4s ease-in-out infinite; }
                .gy-badge-float { animation: gyBadgeFloat 4s ease-in-out infinite; }
                .gy-typing-dot { display: inline-block; width: 6px; height: 6px; border-radius: 9999px; background: #9CA3AF; animation: gyDot 1.2s ease-in-out infinite; }

                /* Header / footer additions */
                .gy-navlink { position: relative; }
                .gy-navlink::after { content: ''; position: absolute; left: 14px; right: 14px; bottom: 6px; height: 2px; background: #25D366; border-radius: 2px; transform: scaleX(0); transform-origin: left; transition: transform 0.25s ease; }
                .gy-navlink:hover::after { transform: scaleX(1); }
                .gy-mobile-menu { transition: max-height 0.35s cubic-bezier(0.22,1,0.36,1), opacity 0.3s ease; }
            `}</style>

            {/* ================= MAIN ================= */}
            <main className="flex-1 relative">
                <div className="absolute top-10 left-4 sm:left-10 w-40 h-40 sm:w-72 sm:h-72 bg-[#25D366] rounded-full mix-blend-multiply filter blur-3xl opacity-[0.08]" style={{ animation: 'gyFloat 9s ease-in-out infinite' }}></div>
                <div className="absolute bottom-10 right-4 sm:right-10 w-48 h-48 sm:w-96 sm:h-96 bg-green-300 rounded-full mix-blend-multiply filter blur-3xl opacity-[0.12]" style={{ animation: 'gyFloatSlow 11s ease-in-out infinite', animationDelay: '2s' }}></div>

                {/* ============ LANDING PAGE (not yet connecting a device) ============ */}
                {!startClicked && (
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 relative z-10">

                        {/* Hero */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-10 items-center mb-20 sm:mb-28">
                            <div className="gy-pop text-center lg:text-left">
                                <div className="inline-flex items-center gap-2.5 bg-[#25D366]/10 text-[#3ce089] pl-2 pr-4 py-2 rounded-full mb-7 border border-[#25D366]/20">
                                    <span className="w-6 h-6 rounded-full bg-[#0B141A] flex items-center justify-center shadow-sm shrink-0">
                                        <FaUserCircle className="text-sm text-[#25D366]" />
                                    </span>
                                    <span className="font-semibold text-xs sm:text-sm tracking-wide">Welcome back to LC, {userName}!</span>
                                </div>

                                <h1 className="gy-display text-[2.75rem] sm:text-6xl md:text-7xl font-black text-white mb-6 tracking-tighter leading-[0.95] break-words">
                                    Reach Thousands <br className="hidden sm:block" />
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#128C7E] via-[#25D366] to-[#128C7E] gy-gradient-text">Instantly.</span>
                                </h1>

                                <div className="text-[#25D366] font-bold text-lg sm:text-xl mb-6 tracking-wide">
                                    Free WhatsApp Bulk Messaging Software
                                </div>

                                <p className="text-white/50 text-base sm:text-lg mb-9 max-w-md mx-auto lg:mx-0 leading-relaxed">
                                    Connect your WhatsApp and start sending bulk messages instantly with LC. A free, fast, and reliable Free WhatsApp bulk messaging software for businesses and teams.
                                </p>

                                <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3.5 mb-10 sm:mb-14">
                                    <button onClick={handleConnectClick} className="gy-shine-btn group flex items-center justify-center gap-2.5 w-full sm:w-auto px-7 py-3.5 sm:py-4 bg-[#25D366] text-white text-sm sm:text-base font-bold rounded-xl shadow-[0_10px_24px_rgba(37,211,102,0.3)] hover:shadow-[0_14px_30px_rgba(37,211,102,0.42)] hover:-translate-y-0.5 transition-all duration-300">
                                        <span className="gy-shine"></span>
                                        <FaWhatsapp className="text-lg group-hover:scale-110 transition-transform" /> Connect Device Now
                                    </button>
                                    <a href="#how-it-works" className="group flex items-center justify-center gap-2.5 w-full sm:w-auto pl-7 pr-3 py-3.5 sm:py-4 text-white text-sm sm:text-base font-bold rounded-xl border border-white/15 bg-white/5 hover:border-[#25D366] hover:bg-[#25D366]/10 transition-all duration-300">
                                        See How It Works
                                        <span className="w-8 h-8 rounded-lg bg-white/10 group-hover:bg-[#25D366] flex items-center justify-center transition-colors duration-300">
                                            <FaArrowRight className="text-xs text-white/50 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
                                        </span>
                                    </a>
                                </div>

                                {/* Stats row */}
                                <div className="flex items-stretch justify-center lg:justify-start gap-6 sm:gap-10 max-w-md mx-auto lg:mx-0 pt-7 border-t border-white/10">
                                    <div className="flex flex-col items-center lg:items-start gap-1.5">
                                        <FaUsers className="text-[#25D366] text-sm" />
                                        <p className="gy-display text-lg sm:text-2xl font-black text-white leading-none">10M+</p>
                                        <p className="text-[11px] sm:text-xs text-white/40 font-semibold uppercase tracking-wide">Messages Sent</p>
                                    </div>
                                    <div className="w-px bg-white/10"></div>
                                    <div className="flex flex-col items-center lg:items-start gap-1.5">
                                        <FaChartLine className="text-[#25D366] text-sm" />
                                        <p className="gy-display text-lg sm:text-2xl font-black text-white leading-none">5,000+</p>
                                        <p className="text-[11px] sm:text-xs text-white/40 font-semibold uppercase tracking-wide">Businesses</p>
                                    </div>
                                    <div className="w-px bg-white/10"></div>
                                    <div className="flex flex-col items-center lg:items-start gap-1.5">
                                        <FaShieldAlt className="text-[#25D366] text-sm" />
                                        <p className="gy-display text-lg sm:text-2xl font-black text-white leading-none">99.9%</p>
                                        <p className="text-[11px] sm:text-xs text-white/40 font-semibold uppercase tracking-wide">Uptime</p>
                                    </div>
                                </div>
                            </div>

                            {/* Premium phone-mockup visual — chat preview surrounded by live stat cards */}
                            <div className="gy-pop relative w-full flex justify-center lg:justify-end" style={{ animationDelay: '150ms' }}>
                                <div className="relative mx-auto w-full max-w-[360px] sm:max-w-[420px] lg:max-w-none lg:w-[600px] lg:h-[600px]">

                                    {/* Ambient glow */}
                                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#25D366]/20 via-green-400/10 to-transparent blur-3xl -z-10"></div>

                                    {/* ===== Phone mockup (always centered) ===== */}
                                    <div className="relative z-20 mx-auto mb-6 w-[240px] sm:w-[262px] lg:absolute lg:top-1/2 lg:left-1/2 lg:-translate-x-1/2 lg:-translate-y-1/2 lg:mb-0">
                                        {/* Soft natural shadow beneath the phone — no rings, no halos */}
                                        <div className="absolute left-1/2 -translate-x-1/2 -bottom-4 w-[70%] h-6 rounded-full bg-black/50 blur-xl -z-10"></div>

                                        <div className="relative rounded-[2.1rem] bg-gradient-to-b from-[#0E1A20] to-[#060B0D] border-[6px] border-[#1B2A30] shadow-[0_25px_55px_rgba(0,0,0,0.5),0_0_30px_rgba(37,211,102,0.12)] overflow-hidden">
                                            {/* Status bar */}
                                            <div className="flex items-center justify-between px-5 pt-3 pb-1">
                                                <span className="text-[10px] font-bold text-white/70 tracking-wide">9:41</span>
                                                <div className="flex items-center gap-1">
                                                    <div className="flex items-end gap-[2px] h-2.5">
                                                        <span className="w-[2.5px] h-[40%] bg-white/50 rounded-sm"></span>
                                                        <span className="w-[2.5px] h-[65%] bg-white/50 rounded-sm"></span>
                                                        <span className="w-[2.5px] h-[85%] bg-white/50 rounded-sm"></span>
                                                        <span className="w-[2.5px] h-full bg-white/70 rounded-sm"></span>
                                                    </div>
                                                    <span className="w-4 h-2.5 rounded-[2px] border border-white/50 relative ml-1">
                                                        <span className="absolute inset-[1.5px] right-[3px] bg-[#25D366] rounded-[1px]"></span>
                                                    </span>
                                                </div>
                                            </div>

                                            {/* App bar */}
                                            <div className="flex items-center justify-between px-4 pt-2.5 pb-4">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span
                                                        className="w-8 h-8 rounded-xl flex items-center justify-center text-white text-xs font-extrabold shrink-0 shadow-[0_4px_12px_rgba(37,211,102,0.35)]"
                                                        style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
                                                    >
                                                        G
                                                    </span>
                                                    <div className="leading-tight min-w-0">
                                                        <p className="text-white text-[13px] font-bold truncate">Goyee</p>
                                                        <p className="text-[9px] text-white/40 truncate">Broadcast Dashboard</p>
                                                    </div>
                                                </div>
                                                <span className="relative w-8 h-8 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/60 shrink-0">
                                                    <FaBell size={12} />
                                                    <span className="absolute top-1.5 right-2 w-1.5 h-1.5 rounded-full bg-[#25D366]"></span>
                                                </span>
                                            </div>

                                            {/* Hero: live campaign progress */}
                                            <div className="mx-4 rounded-2xl bg-white/[0.04] border border-white/10 p-3.5 flex items-center gap-3.5 mb-4">
                                                <div className="relative w-[62px] h-[62px] shrink-0">
                                                    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                                                        <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="9" />
                                                        <circle
                                                            cx="50" cy="50" r="42" fill="none" stroke="#25D366" strokeWidth="9"
                                                            strokeLinecap="round" strokeDasharray="264" strokeDashoffset="58"
                                                        />
                                                    </svg>
                                                    <div className="absolute inset-0 flex items-center justify-center">
                                                        <span className="text-white font-black text-[13px]">78%</span>
                                                    </div>
                                                </div>
                                                <div className="min-w-0 leading-tight">
                                                    <p className="text-white text-[12.5px] font-bold truncate">Campaign Live</p>
                                                    <p className="text-[10px] text-white/45 mt-0.5 truncate">9,842 of 12,600 delivered</p>
                                                    <span className="inline-flex items-center gap-1.5 mt-1.5 text-[9.5px] font-semibold text-[#25D366]">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-[#25D366] animate-pulse"></span>
                                                        Sending now
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Quick metrics */}
                                            <div className="grid grid-cols-3 gap-2 px-4 mb-4">
                                                {[
                                                    { label: 'Sent', value: '12.4K' },
                                                    { label: 'Read', value: '98%' },
                                                    { label: 'Failed', value: '0.2%' },
                                                ].map((m) => (
                                                    <div key={m.label} className="rounded-xl bg-white/[0.03] border border-white/[0.06] py-2 text-center">
                                                        <p className="text-white text-[12px] font-extrabold leading-tight">{m.value}</p>
                                                        <p className="text-[8.5px] text-white/40 mt-0.5">{m.label}</p>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Recent campaigns */}
                                            <div className="px-4 flex items-center justify-between mb-2">
                                                <p className="text-white text-[11px] font-bold">Recent Campaigns</p>
                                                <p className="text-[9.5px] text-[#25D366] font-semibold">See all</p>
                                            </div>
                                            <div className="px-4 space-y-2 mb-4">
                                                {[
                                                    { name: 'Diwali Offer Blast', meta: '4,200 contacts', icon: <FaCheckDouble />, tone: '#25D366' },
                                                    { name: 'Order Reminders', meta: '1,860 contacts', icon: <FaBolt />, tone: '#F5A623' },
                                                    { name: 'New Signups', meta: '980 contacts', icon: <FaUsers />, tone: '#2E9BFF' },
                                                ].map((row) => (
                                                    <div key={row.name} className="flex items-center gap-2.5 rounded-xl bg-white/[0.03] px-3 py-2 border border-white/[0.05]">
                                                        <span
                                                            className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-[10px] shrink-0"
                                                            style={{ backgroundColor: `${row.tone}26`, color: row.tone }}
                                                        >
                                                            {row.icon}
                                                        </span>
                                                        <div className="min-w-0 leading-tight flex-1">
                                                            <p className="text-white text-[10.5px] font-semibold truncate">{row.name}</p>
                                                            <p className="text-[8.5px] text-white/40 truncate">{row.meta}</p>
                                                        </div>
                                                        <FaCheckCircle className="text-[#25D366] text-[10px] shrink-0" />
                                                    </div>
                                                ))}
                                            </div>

                                            {/* New broadcast CTA */}
                                            <div className="px-4 pb-3">
                                                <div
                                                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-white text-[12px] font-bold shadow-[0_6px_16px_rgba(37,211,102,0.35)]"
                                                    style={{ background: 'linear-gradient(135deg, #25D366, #128C7E)' }}
                                                >
                                                    <FaPlus size={10} /> New Broadcast
                                                </div>
                                            </div>

                                            {/* Bottom nav */}
                                            <div className="flex items-center justify-between px-8 py-3 border-t border-white/[0.06] bg-[#0B141A]/60">
                                                <FaHome className="text-[#25D366] text-[13px]" />
                                                <FaCommentDots className="text-white/30 text-[13px]" />
                                                <FaAddressBook className="text-white/30 text-[13px]" />
                                                <FaCog className="text-white/30 text-[13px]" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* ===== Floating live stat cards ===== */}
                                    <div className="relative z-10 grid grid-cols-2 gap-3 sm:gap-4 lg:block">
                                        {[
                                            { icon: <FaUsers />, label: 'Contacts', value: '2,358', sub: 'Total Contacts', pos: 'lg:absolute lg:top-2 lg:left-0', delay: '0ms', swatch: 'linear-gradient(135deg, #25D366, #128C7E)' },
                                            { icon: <FaHeartbeat />, label: 'Active Now', value: '1,248', sub: 'Users Online', pos: 'lg:absolute lg:top-2 lg:right-0', delay: '90ms', swatch: 'linear-gradient(135deg, #25D366, #128C7E)' },
                                            { icon: <FaCheckCircle />, label: 'Message Sent', value: '156K', sub: 'This Month', pos: 'lg:absolute lg:top-1/2 lg:-translate-y-1/2 lg:left-[-10px] xl:left-[-32px]', delay: '180ms', swatch: 'linear-gradient(135deg, #25D366, #128C7E)' },
                                            { icon: <FaShieldAlt />, label: 'Secure', value: '100%', sub: 'Trusted Platform', pos: 'lg:absolute lg:top-1/2 lg:-translate-y-1/2 lg:right-[-10px] xl:right-[-32px]', delay: '270ms', swatch: 'linear-gradient(135deg, #2E9BFF, #1467C7)' },
                                            { icon: <FaCommentDots />, label: 'Auto Replies', value: 'Active', sub: 'Always On', pos: 'lg:absolute lg:bottom-2 lg:left-0', delay: '360ms', swatch: 'linear-gradient(135deg, #A855F7, #7C3AED)' },
                                            { icon: <FaChartBar />, label: 'Delivery Rate', value: '98.7%', sub: 'Excellent', pos: 'lg:absolute lg:bottom-2 lg:right-0', delay: '450ms', swatch: 'linear-gradient(135deg, #F5A623, #D97706)' },
                                        ].map((c, i) => (
                                            <div
                                                key={c.label}
                                                className={`gy-pop gy-badge-float ${c.pos} flex items-center gap-2.5 bg-[#111B21] px-3.5 py-3 rounded-2xl shadow-xl border border-white/10 hover:border-[#25D366]/40 hover:-translate-y-1 transition-all duration-300 lg:w-[168px]`}
                                                style={{ animationDelay: c.delay }}
                                            >
                                                <div
                                                    className="w-9 h-9 shrink-0 rounded-full text-white flex items-center justify-center text-sm shadow-[0_4px_14px_rgba(0,0,0,0.35)]"
                                                    style={{ background: c.swatch }}
                                                >
                                                    {c.icon}
                                                </div>
                                                <div className="min-w-0 leading-tight">
                                                    <p className="text-[10px] font-semibold text-white/50 truncate">{c.label}</p>
                                                    <p className="gy-display text-sm sm:text-base font-black text-white truncate">{c.value}</p>
                                                    <p className="text-[9px] text-white/35 truncate">{c.sub}</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Feature strip */}
                        <div id="features" className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-7 mb-24 sm:mb-32 scroll-mt-24">
                            <div className="gy-pop gy-tilt bg-white/[0.03] p-6 sm:p-8 rounded-3xl shadow-[0_2px_16px_rgba(0,0,0,0.2)] border border-white/10 hover:shadow-[0_20px_40px_rgba(37,211,102,0.18)] hover:border-[#25D366]/40 transition-all duration-300" style={{ animationDelay: '0ms' }}>
                                <div className="w-14 h-14 bg-[#25D366]/10 text-[#25D366] rounded-2xl flex items-center justify-center mb-5 text-2xl"><FaBolt /></div>
                                <h3 className="gy-display text-lg sm:text-xl font-bold text-white mb-2">Bulk Sending</h3>
                                <p className="text-sm text-white/50 leading-relaxed">Send thousands of personalized messages in a single click, instantly.</p>
                            </div>
                            <div className="gy-pop gy-tilt bg-white/[0.03] p-6 sm:p-8 rounded-3xl shadow-[0_2px_16px_rgba(0,0,0,0.2)] border border-white/10 hover:shadow-[0_20px_40px_rgba(37,211,102,0.18)] hover:border-[#25D366]/40 transition-all duration-300" style={{ animationDelay: '120ms' }}>
                                <div className="w-14 h-14 bg-[#25D366]/10 text-[#25D366] rounded-2xl flex items-center justify-center mb-5 text-2xl"><FaFileCsv /></div>
                                <h3 className="gy-display text-lg sm:text-xl font-bold text-white mb-2">Easy Import</h3>
                                <p className="text-sm text-white/50 leading-relaxed">Upload an Excel or CSV file and your contact list is ready to go.</p>
                            </div>
                            <div className="gy-pop gy-tilt bg-white/[0.03] p-6 sm:p-8 rounded-3xl shadow-[0_2px_16px_rgba(0,0,0,0.2)] border border-white/10 hover:shadow-[0_20px_40px_rgba(37,211,102,0.18)] hover:border-[#25D366]/40 transition-all duration-300" style={{ animationDelay: '240ms' }}>
                                <div className="w-14 h-14 bg-[#25D366]/10 text-[#25D366] rounded-2xl flex items-center justify-center mb-5 text-2xl"><FaShieldAlt /></div>
                                <h3 className="gy-display text-lg sm:text-xl font-bold text-white mb-2">Secure Sessions</h3>
                                <p className="text-sm text-white/50 leading-relaxed">Your WhatsApp session stays encrypted and private, end to end.</p>
                            </div>
                        </div>

                        {/* How it works */}
                        <div id="how-it-works" className="gy-pop relative bg-white/[0.03] rounded-[1.5rem] sm:rounded-[3rem] shadow-[0_2px_24px_rgba(0,0,0,0.25)] p-6 sm:p-14 border border-white/10 overflow-visible scroll-mt-24">
                            <div className="text-center mb-10 sm:mb-14">
                                <div className="inline-block px-4 py-2 bg-[#25D366]/10 text-[#3ce089] font-bold text-xs tracking-widest uppercase rounded-full mb-4 border border-[#25D366]/20">Getting Started</div>
                                <h2 className="gy-display text-2xl sm:text-4xl font-extrabold text-white">Live in three simple steps</h2>
                            </div>
                            {/*
                              Fix: the previous "sm:grid-cols-3" switched to a 3-column layout at 640px,
                              a width many phones/phablets (and landscape phones) still hit, which squeezed
                              step 3 into a too-narrow column and made it look clipped with no way to scroll
                              to it. Columns now stay stacked (grid-cols-1, full width, natural page scroll)
                              all the way up to the "md" breakpoint (768px), which is the point where there's
                              actually enough room for 3 columns side by side. Desktop/tablet (md+) is unchanged.
                            */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 md:gap-6 relative overflow-visible">
                                <div className="hidden md:block absolute top-8 left-[16.5%] right-[16.5%] h-0.5 bg-gradient-to-r from-green-200 via-[#25D366] to-green-200"></div>
                                {[
                                    { step: "1", title: "Scan QR Code", desc: "Link your WhatsApp in seconds, just like WhatsApp Web.", icon: <FaWhatsapp /> },
                                    { step: "2", title: "Upload Contacts", desc: "Add numbers manually or import a CSV / Excel file.", icon: <FaFileCsv /> },
                                    { step: "3", title: "Send & Track", desc: "Fire off your campaign and watch delivery in real time.", icon: <FaChartLine /> },
                                ].map((s) => (
                                    <div key={s.step} className="relative flex flex-col items-center text-center min-w-0 w-full">
                                        <div className="relative z-10 w-16 h-16 rounded-2xl bg-gradient-to-tr from-[#128C7E] to-[#25D366] text-white flex items-center justify-center text-2xl shadow-lg mb-4 shrink-0">
                                            {s.icon}
                                        </div>
                                        <span className="text-xs font-black text-[#25D366] tracking-widest mb-1">STEP {s.step}</span>
                                        <h3 className="gy-display text-lg font-bold text-white mb-2">{s.title}</h3>
                                        <p className="text-sm text-white/50 max-w-[220px] mx-auto">{s.desc}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Bottom safe-space so the last section is never flush against the viewport edge on mobile */}
                        <div className="h-6 sm:h-0" aria-hidden="true"></div>
                    </div>
                )}

                {/* ============ CONNECT / QR / FORM FLOW (unchanged logic) ============ */}
                {startClicked && (
                <div className="flex flex-col items-center justify-center p-4 sm:p-6 gap-8 min-h-[70vh] relative z-10">
                <div className={`gy-pop relative z-10 w-full bg-[#111B21]/90 backdrop-blur-2xl rounded-2xl sm:rounded-3xl shadow-[0_20px_50px_rgba(37,211,102,0.15)] border border-white/10 overflow-hidden transition-all duration-700 ease-out flex flex-col items-center justify-center min-h-[400px]
                    ${isAuthenticated && !showFormOnly ? 'max-w-2xl' : 
                      (isAuthenticated && showFormOnly ? 'max-w-5xl' : 'max-w-md')}`}>

                    {/* 2. QR Screen */}
                    {startClicked && !isAuthenticated && (
                        <div className="flex flex-col items-center justify-center p-6 sm:p-10 w-full animate-fade-in-up">
                            <h2 className="gy-display text-2xl sm:text-3xl font-extrabold mb-6 sm:mb-8 text-center text-[#25D366]">Secure Link</h2>
                            <div className="flex flex-col items-center w-full">
                                {connectionStatus === "disconnected" ? (
                                    <div className="flex flex-col items-center py-8 sm:py-12 text-center">
                                        <div className="relative w-16 h-16 sm:w-20 sm:h-20 mb-6"><div className="absolute inset-0 border-4 border-green-200 rounded-full"></div><div className="absolute inset-0 border-4 border-[#25D366] rounded-full border-t-transparent animate-spin"></div></div>
                                        <p className="text-white/80 font-semibold">Connecting to WhatsApp server...</p>
                                        <p className="text-sm text-white/50 mt-2 px-4">If this continues, start the backend server on port 5000.</p>
                                    </div>
                                ) : qrCode ? (
                                    <div className="relative p-3 sm:p-4 bg-white border-4 border-dashed rounded-2xl shadow-[0_0_40px_rgba(37,211,102,0.25)] mb-6 hover:border-solid hover:scale-105 transition-all duration-300 cursor-pointer max-w-full overflow-hidden" style={{ borderColor: "#25D366" }}>
                                        <QRCode value={qrCode} size={220} style={{ maxWidth: '100%', height: 'auto', width: '100%' }} />
                                        <span className="gy-scan-line"></span>
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center py-8 sm:py-12">
                                        <div className="relative w-16 h-16 sm:w-20 sm:h-20 mb-6"><div className="absolute inset-0 border-4 border-green-200 rounded-full"></div><div className="absolute inset-0 border-4 border-[#25D366] rounded-full border-t-transparent animate-spin"></div></div>
                                        <p className="text-white/50 font-medium animate-pulse text-center px-4">Establishing secure connection...</p>
                                    </div>
                                )}
                                <p className="text-sm text-white/40 mt-2 text-center px-2">Open WhatsApp &gt; Linked Devices &gt; Scan</p>
                                <button onClick={() => setStartClicked(false)} className="mt-6 text-sm text-white/40 hover:text-white transition-colors underline">Cancel & Go Back</button>
                            </div>
                        </div>
                    )}

                    {/* 3. Auth Success */}
                    {startClicked && isAuthenticated && !showFormOnly && (
                        <div className="flex flex-col items-center justify-center p-6 sm:p-12 w-full h-full bg-gradient-to-b from-[#25D366]/10 to-transparent animate-fade-in-up">
                            <h2 className="gy-display text-3xl sm:text-4xl font-extrabold mb-6 sm:mb-8 text-center text-[#25D366]">Success!</h2>
                            <div className="flex flex-col items-center text-center">
                                <div className="relative rounded-full bg-[#25D366]/10 p-6 sm:p-8 mb-6 shadow-inner flex items-center justify-center">
                                    <span className="gy-ring" style={{ animationDelay: '0s' }}></span>
                                    <span className="gy-ring" style={{ animationDelay: '0.5s' }}></span>
                                    <span className="gy-ring" style={{ animationDelay: '1s' }}></span>
                                    <FaCheckCircle className="relative text-6xl sm:text-8xl text-[#25D366] animate-bounce" />
                                </div>
                                <h3 className="gy-display text-2xl sm:text-3xl text-white font-bold mb-2">Device Linked</h3>
                                <p className="text-base sm:text-lg text-white/50">Preparing your dashboard...</p>
                            </div>
                        </div>
                    )}

                    {/* 4. Form Panel */}
                    {startClicked && (isAuthenticated || showFormOnly) && (
                        <div className={`p-4 sm:p-8 lg:p-10 w-full flex flex-col transition-all duration-700 ease-in-out ${showFormOnly ? 'w-full opacity-100 translate-x-0' : 'md:w-2/3 opacity-0 translate-x-10'}`}>

                            {/* WhatsApp Status Banner */}
                            <div className="flex flex-wrap items-center justify-between gap-3 bg-[#25D366]/10 border border-[#25D366]/20 rounded-xl px-4 py-3 mb-6 animate-fade-in-up">
                                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#25D366]">
                                    <div className="w-2 h-2 bg-[#25D366] rounded-full animate-ping"></div>
                                    Sender Online
                                </div>
                                <div className="text-sm font-mono font-bold text-white/80">
                                    {connectedUser && connectedUser.id ? `+${connectedUser.id}` : "Loading..."}
                                </div>
                            </div>

                            {/* Header */}
                            <div className="flex flex-wrap items-start justify-between gap-3 mb-6 sm:mb-8 pb-5 sm:pb-6 border-b border-white/10">
                                <div className="flex items-center gap-3">
                                    <span className="w-10 h-10 rounded-xl bg-[#25D366]/15 border border-[#25D366]/30 flex items-center justify-center text-[#25D366] text-base shrink-0">
                                        <FaPaperPlane />
                                    </span>
                                    <div>
                                        <h2 className="gy-display text-xl sm:text-2xl font-extrabold text-white leading-tight">Message Configuration</h2>
                                        <p className="text-xs sm:text-sm text-white/40 mt-0.5">Add your recipients, craft your message, and send</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 bg-[#25D366]/10 text-[#25D366] px-3 py-1.5 rounded-full text-xs font-bold border border-[#25D366]/30 shadow-sm shrink-0">
                                    <div className="w-2 h-2 bg-[#25D366] rounded-full animate-ping"></div> Online
                                </div>
                            </div>

                            {/* Two-column layout: Recipients | Message */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6">

                                {/* Recipients */}
                                <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-4 sm:p-6 space-y-5">
                                    <div className="flex items-center justify-between">
                                        <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                            <FaUsers className="text-[#25D366]" /> Recipients
                                        </h3>
                                        {recipientCount > 0 && (
                                            <span className="text-[11px] font-semibold text-white/50 bg-white/5 border border-white/10 px-2.5 py-1 rounded-full">
                                                {recipientCount} number{recipientCount === 1 ? '' : 's'}
                                            </span>
                                        )}
                                    </div>
                                    <div className="group relative">
                                        <label className="block text-xs font-semibold text-white/50 mb-2 uppercase tracking-wide">Contacts File (CSV/Excel)</label>
                                        <div className="relative border-2 border-dashed border-white/15 rounded-xl p-4 sm:p-5 text-center hover:border-[#25D366] hover:bg-[#25D366]/5 transition-all duration-300 cursor-pointer">
                                            <FaCloudUploadAlt className="mx-auto text-2xl sm:text-3xl text-white/30 group-hover:text-[#25D366] group-hover:-translate-y-1 transition-all duration-300 mb-2" />
                                            <p className="text-xs sm:text-sm text-white/50 group-hover:text-white/70 break-words px-2">
                                                {excelFile ? (
                                                    <span className="inline-flex items-center gap-2 text-[#25D366] font-bold">
                                                        {excelFile.name}
                                                        <button
                                                            type="button"
                                                            onClick={removeExcelFile}
                                                            aria-label="Remove selected file"
                                                            title="Remove file"
                                                            className="relative z-10 inline-flex items-center justify-center w-5 h-5 rounded-full text-white/50 hover:text-red-400 hover:bg-white/10 transition-colors duration-200"
                                                        >
                                                            <FaTimes className="text-[10px]" />
                                                        </button>
                                                    </span>
                                                ) : (
                                                    <>Drag & Drop or <span className="text-[#25D366] font-semibold">Choose File</span></>
                                                )}
                                            </p>
                                            <input ref={excelInputRef} type="file" accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => setExcelFile(e.target.files[0])} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                                        </div>
                                        
                                        {/* Download Sample Excel Template Card */}
                                        <div className="mt-4 p-4 bg-white/[0.02] border border-white/[0.08] rounded-2xl relative overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.2)] hover:border-[#25D366]/30 transition-all duration-300 flex gap-3 text-left">
                                            
                                            {/* Excel Icon Illustration */}
                                            <div className="flex items-center justify-center shrink-0 w-8 h-8 rounded-lg bg-[#25D366]/10 text-[#25D366] border border-[#25D366]/20 self-start mt-0.5">
                                                <FaFileCsv className="text-lg" />
                                            </div>

                                            {/* Card Contents */}
                                            <div className="flex-1 flex flex-col min-w-0">
                                                <h4 className="text-xs sm:text-sm font-bold text-white mb-1 leading-tight">
                                                    📄 Need an Excel Template?
                                                </h4>
                                                <p className="text-[10px] sm:text-xs text-white/40 leading-relaxed font-medium mb-3">
                                                    Download our ready-to-use template, replace sample numbers with your contacts, and upload it here.
                                                </p>

                                                {/* Download Button */}
                                                <a 
                                                    href="/contacts_template.xlsx" 
                                                    download="contacts_template.xlsx"
                                                    className="w-full sm:w-auto self-start flex items-center justify-center gap-2 py-2 px-4 bg-[#25D366] hover:bg-[#128C7E] text-[#0a0f0c] hover:text-white font-extrabold rounded-lg text-[11px] sm:text-xs shadow-md transition-all duration-300 cursor-pointer"
                                                >
                                                    <FaDownload />
                                                    Download Template
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-white/50 mb-2 uppercase tracking-wide">Manual Contacts</label>
                                        {/* Logic for multiple input boxes */}
                                        <div className="space-y-2.5 max-h-[240px] overflow-y-auto pr-1">
                                            {manualContacts.map((contact, index) => (
                                                <div key={index} className="relative w-full group">
                                                    <input 
                                                        type="text" 
                                                        value={contact}
                                                        onChange={(e) => handleContactChange(index, e.target.value)}
                                                        placeholder={index === 0 ? "e.g. 917418888999, 917418888998" : "Phone Number"} 
                                                        className={`w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/30 outline-none focus:bg-white/[0.07] focus:border-[#25D366] focus:ring-4 focus:ring-[#25D366]/10 transition-all duration-300 focus:scale-[1.01] ${
                                                            index > 0 ? "pr-12" : ""
                                                        }`} 
                                                    />
                                                    {index > 0 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeContactField(index)}
                                                            className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-7 h-7 rounded-lg text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
                                                            title="Delete contact field"
                                                        >
                                                            <FaTimes className="text-xs" />
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                        <button onClick={addContactField} className="text-xs sm:text-sm text-[#25D366] font-semibold mt-3 flex items-center gap-1 hover:gap-2 transition-all">
                                            <FaPlus className="text-[10px]" /> Add another contact
                                        </button>
                                    </div>
                                </div>

                                {/* Message */}
                                <div className="bg-white/[0.03] border border-white/[0.07] rounded-2xl p-4 sm:p-6 flex flex-col space-y-5">
                                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                                        <FaCommentDots className="text-[#25D366]" /> Message
                                    </h3>

                                    <div>
                                        <label className="block text-xs font-semibold text-white/50 mb-2 uppercase tracking-wide">Text</label>
                                        <textarea 
                                            rows="6" 
                                            value={message}
                                            onChange={(e) => setMessage(e.target.value)}
                                            placeholder="Type your message here..."
                                            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder:text-white/30 outline-none focus:bg-white/[0.07] focus:border-[#25D366] focus:ring-4 focus:ring-[#25D366]/10 transition-all duration-300 resize-none shadow-inner"
                                        ></textarea>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-white/50 mb-2 uppercase tracking-wide">
                                            Media / Document <span className="normal-case text-white/30">(optional)</span>
                                        </label>
                                        <div className="relative border-2 border-dashed border-white/15 rounded-xl px-4 py-3.5 flex items-center gap-3 hover:border-[#25D366] hover:bg-[#25D366]/5 transition-all duration-300 cursor-pointer">
                                            <FaCloudUploadAlt className="text-xl text-white/30 shrink-0" />
                                            <span className="text-sm text-white/50 truncate">
                                                {mediaFile ? (
                                                    <span className="text-[#25D366] font-semibold">{mediaFile.filename}</span>
                                                ) : (
                                                    "Attach an image, video, or document"
                                                )}
                                            </span>
                                            {mediaFile && (
                                                <button
                                                    type="button"
                                                    onClick={removeMediaFile}
                                                    aria-label="Remove selected file"
                                                    title="Remove file"
                                                    className="relative z-10 inline-flex items-center justify-center w-5 h-5 rounded-full text-white/50 hover:text-red-400 hover:bg-white/10 transition-colors duration-200 shrink-0"
                                                >
                                                    <FaTimes className="text-[10px]" />
                                                </button>
                                            )}
                                            <input ref={mediaInputRef} type="file" onChange={handleMediaChange} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
                                        </div>
                                    </div>

                                    {/* Scheduler Toggle Disabled for now
                                    <div className="flex flex-col gap-4 mt-6 p-4 bg-[#1E1E1E] rounded-xl border border-[#333]">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className={`w-12 h-6 rounded-full p-1 cursor-pointer transition-colors ${isScheduling ? 'bg-[#25D366]' : 'bg-gray-600'}`} onClick={() => setIsScheduling(!isScheduling)}>
                                                    <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform ${isScheduling ? 'translate-x-6' : 'translate-x-0'}`}></div>
                                                </div>
                                                <span className="font-semibold text-white/80">Schedule this message for later</span>
                                            </div>
                                        </div>
                                        
                                        {isScheduling && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-[#333] animate-fade-in-up">
                                                <div className="flex flex-col">
                                                    <label className="block text-sm font-medium text-white/60 mb-1">Date</label>
                                                    <input type="date" min={new Date().toISOString().split('T')[0]} value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} className="w-full sm:w-44 h-[46px] px-3 border border-[#444] rounded-lg focus:ring-2 focus:ring-[#25D366] focus:border-transparent outline-none bg-[#2A2A2A] text-white" />
                                                </div>
                                                <div className="flex flex-col">
                                                    <label className="block text-sm font-medium text-white/60 mb-1">Time</label>
                                                    <div className="flex items-center gap-1">
                                                        <input 
                                                            type="text" 
                                                            placeholder="HH" 
                                                            value={scheduleHour} 
                                                            onChange={e => {
                                                                const v = e.target.value.replace(/\D/g, '').slice(0, 2);
                                                                if (!v || parseInt(v) <= 12) {
                                                                    setScheduleHour(v);
                                                                    if (v.length === 2 && parseInt(v) >= 1 && parseInt(v) <= 12) {
                                                                        const minInput = document.getElementById('schedule-min-input');
                                                                        if (minInput) minInput.focus();
                                                                    }
                                                                }
                                                            }}
                                                            onBlur={(e) => {
                                                                if (e.target.value && parseInt(e.target.value) >= 1) {
                                                                    setScheduleHour(e.target.value.padStart(2, '0'));
                                                                } else {
                                                                    setScheduleHour("12");
                                                                }
                                                            }}
                                                            className="w-12 h-[46px] px-2 border border-[#444] rounded-lg focus:ring-2 focus:ring-[#25D366] focus:border-transparent outline-none bg-[#2A2A2A] text-white text-center font-semibold" 
                                                        />
                                                        <span className="text-xl font-bold text-gray-400">:</span>
                                                        <input 
                                                            id="schedule-min-input"
                                                            type="text" 
                                                            placeholder="MM" 
                                                            value={scheduleMinute} 
                                                            onChange={e => {
                                                                const v = e.target.value.replace(/\D/g, '').slice(0, 2);
                                                                if (!v || parseInt(v) <= 59) {
                                                                    setScheduleMinute(v);
                                                                }
                                                            }}
                                                            onBlur={(e) => {
                                                                if (e.target.value) {
                                                                    setScheduleMinute(e.target.value.padStart(2, '0'));
                                                                } else {
                                                                    setScheduleMinute("00");
                                                                }
                                                            }}
                                                            className="w-12 h-[46px] px-2 border border-[#444] rounded-lg focus:ring-2 focus:ring-[#25D366] focus:border-transparent outline-none bg-[#2A2A2A] text-white text-center font-semibold" 
                                                        />
                                                        <select value={scheduleAmPm} onChange={e => setScheduleAmPm(e.target.value)} className="w-[65px] h-[46px] pl-1 pr-4 py-0 border border-[#444] rounded-lg focus:ring-2 focus:ring-[#25D366] focus:border-transparent outline-none bg-[#2A2A2A] font-bold text-[#25D366] cursor-pointer">
                                                            <option value="AM">AM</option>
                                                            <option value="PM">PM</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                {(scheduleDate && scheduleHour && scheduleMinute) && (
                                                    <div className="col-span-full mt-4 text-sm text-center text-[#25D366] bg-[#25D366]/10 p-3 rounded-xl border border-[#25D366]/30">
                                                        Will be scheduled for <strong className="text-[#34E38A]">{new Date(`${scheduleDate}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}, {scheduleHour}:{scheduleMinute} {scheduleAmPm}</strong>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    */}

                                    <button onClick={sendBulkMessages} className="gy-shine-btn mt-auto flex items-center justify-center gap-2 w-full py-3.5 sm:py-4 text-white font-bold rounded-xl shadow-[0_10px_20px_rgba(37,211,102,0.3)] hover:shadow-[0_15px_30px_rgba(37,211,102,0.4)] hover:-translate-y-1 transition-all duration-300" style={{ backgroundColor: "#25D366" }}>
                                        <span className="gy-shine"></span>
                                        <FaPaperPlane /> Send Messages
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
                </div>
                )}
            </main>

            {/* ================= FOOTER ================= */}
            <footer className="relative bg-[#212122] mt-auto">
                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#25D366]/50 to-transparent"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10 sm:gap-8 mb-10 sm:mb-14">

                        {/* Brand */}
                        <div className="sm:col-span-2 md:col-span-2">
                            <div className="flex items-center gap-2.5 mb-4">
                                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#25D366] to-[#128C7E] flex items-center justify-center shadow-[0_4px_14px_rgba(37,211,102,0.3)]">
                                    <FaWhatsapp className="text-white text-lg" />
                                </div>
                                <p className="gy-display text-white font-bold text-lg tracking-tight">Goyee <span className="text-[#25D366]">Broadcast</span></p>
                            </div>
                            <p className="text-sm text-white/50 leading-relaxed max-w-sm mb-5">
                                Connect your WhatsApp and reach your entire audience with personalized bulk messages &mdash; no coding, no hassle.
                            </p>
                            <div className="flex items-center gap-2 text-xs font-semibold text-white/40">
                                <div className="w-1.5 h-1.5 rounded-full bg-[#25D366] animate-pulse"></div>
                                Secure, encrypted sessions
                            </div>
                        </div>

                        {/* Navigate */}
                        <div>
                            <p className="text-white text-sm font-bold tracking-wide uppercase mb-4">Navigate</p>
                            <ul className="space-y-3">
                                <li><a href="#gy-top" className="text-sm text-white/50 hover:text-[#25D366] transition-colors">Home</a></li>
                                <li><a href="#features" className="text-sm text-white/50 hover:text-[#25D366] transition-colors">Features</a></li>
                                <li><a href="#how-it-works" className="text-sm text-white/50 hover:text-[#25D366] transition-colors">How It Works</a></li>
                            </ul>
                        </div>

                        {/* Capabilities */}
                        <div>
                            <p className="text-white text-sm font-bold tracking-wide uppercase mb-4">Capabilities</p>
                            <ul className="space-y-3">
                                <li className="text-sm text-white/50 flex items-center gap-2"><FaBolt className="text-[#25D366] text-xs shrink-0" /> Bulk Sending</li>
                                <li className="text-sm text-white/50 flex items-center gap-2"><FaFileCsv className="text-[#25D366] text-xs shrink-0" /> Easy Import</li>
                                <li className="text-sm text-white/50 flex items-center gap-2"><FaShieldAlt className="text-[#25D366] text-xs shrink-0 cursor-pointer" onClick={() => navigate('/goyee-admin-login')} /> Secure Sessions</li>
                            </ul>
                        </div>
                    </div>

                    <div className="pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <p className="text-xs text-white/35 text-center sm:text-left">&copy; {new Date().getFullYear()} Goyee Broadcast. All rights reserved.</p>
                        <p className="text-xs text-white/25 text-center sm:text-right">WhatsApp is a trademark of Meta Platforms, Inc. Goyee Broadcast is an independent tool and is not affiliated with WhatsApp.</p>
                    </div>
                </div>
            </footer>

            {/* ============ SEND PROGRESS POPUP ============ */}
            {sendProgress && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in-up">
                    <div className="gy-pop w-full max-w-md bg-[#111B21] border border-white/10 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden p-8 text-center">
                        <div className="flex justify-between items-start mb-2">
                            <div className="w-6 h-6"></div> {/* Spacer */}
                            <h3 className="gy-display text-xl font-bold text-white">
                                {sendProgress.done ? "Send Complete" : (isScheduling ? "Scheduling Messages..." : "Sending Bulk Messages...")}
                            </h3>
                            {sendProgress.done ? (
                                <button
                                    type="button"
                                    onClick={() => setSendProgress(null)}
                                    aria-label="Close"
                                    className="w-6 h-6 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/10 transition-colors duration-200"
                                >
                                    <FaTimes className="text-xs" />
                                </button>
                            ) : (
                                <div className="w-6 h-6"></div>
                            )}
                        </div>
                        <p className="text-sm text-white/50 mb-6">
                            {sendProgress.done ? "Your message campaign has finished." : `Please wait while your messages are being ${isScheduling ? "scheduled" : "sent"}.`}
                        </p>
                        
                        {/* Progress Bar Line */}
                        <div className="w-full bg-white/10 rounded-full h-3 mb-2 overflow-hidden">
                            <div 
                                className="bg-[#25D366] h-3 rounded-full transition-all duration-300" 
                                style={{ width: `${sendProgress.total > 0 ? (sendProgress.processed / sendProgress.total) * 100 : 0}%` }}
                            ></div>
                        </div>
                        
                        {/* Percentage Text */}
                        <p className="text-sm font-bold text-[#25D366] mb-6">
                            {Math.round(sendProgress.total > 0 ? (sendProgress.processed / sendProgress.total) * 100 : 0)}%
                        </p>

                        <div className="grid grid-cols-2 gap-4 mb-6">
                            {/* Processed Count Box */}
                            <div className="bg-[#25D366]/10 p-3 rounded-xl border border-[#25D366]/20">
                                <p className="text-xs text-[#25D366] font-bold uppercase">Processed</p>
                                <p className="text-lg font-extrabold text-white mt-1">
                                    {sendProgress.processed} / {sendProgress.total}
                                </p>
                            </div>
                            {/* Failed Count Box */}
                            <div className="bg-red-500/10 p-3 rounded-xl border border-red-500/20">
                                <p className="text-xs text-red-400 font-bold uppercase">Failed</p>
                                <p className="text-lg font-extrabold text-white mt-1">
                                    {sendProgress.failed}
                                </p>
                            </div>
                        </div>
                        
                        {/* Currently Sending Number Display */}
                        {!sendProgress.done && (
                            <div className="bg-white/[0.03] p-3 rounded-xl text-left border border-white/[0.07] mb-6">
                                <p className="text-xs text-white/40 font-semibold mb-1">
                                    Currently {isScheduling ? "Scheduling" : "Sending"}:
                                </p>
                                <p className="font-mono text-sm font-bold text-white/80 truncate">
                                    {sendProgress.currentNumber || "Initializing..."}
                                </p>
                            </div>
                        )}

                        {/* Failed Numbers List */}
                        {failedList.length > 0 && (
                            <div className="bg-black/20 rounded-xl p-3 border border-white/5 max-h-40 overflow-y-auto mb-6 text-left space-y-2">
                                <p className="text-xs font-bold text-red-400 uppercase tracking-wide mb-2">Failure Log</p>
                                {failedList.map((item, idx) => (
                                    <div key={idx} className="text-xs border-b border-white/5 pb-2 last:border-b-0 last:pb-0">
                                        <div className="font-bold text-white flex items-center justify-between">
                                            <span>❌ {item.number}</span>
                                            <span className="text-[10px] text-white/40">{item.time}</span>
                                        </div>
                                        <div className="text-white/50 mt-1">
                                            Reason : <span className="text-red-400/90">{item.reason}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {sendProgress.done ? (
                            <button
                                type="button"
                                onClick={() => setSendProgress(null)}
                                className="w-full py-3 text-sm font-bold text-white rounded-xl transition-all duration-300 hover:-translate-y-0.5"
                                style={{ backgroundColor: "#25D366" }}
                            >
                                Done
                            </button>
                        ) : (
                            <p className="text-xs text-white/30">Please keep this tab open until sending finishes.</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}